import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Un hook de React que detecta cuándo el usuario empieza y deja de scrollear,
 * determinando la dirección, la distancia recorrida y la posición final al detenerse.
 *
 * @param {object} options - Opciones de configuración para el hook.
 * @param {function} [options.onScrollStart] - Función de callback a ejecutar cuando el usuario comienza a scrollear.
 *                                          Recibe la posición inicial del scroll (scrollY).
 * @param {function} [options.onScrollStop] - Función de callback a ejecutar cuando el usuario deja de scrollear.
 *                                         Recibe un objeto con { direction, distance, startPosition, endPosition }.
 * @param {number} [options.stopDelay=150] - El tiempo en milisegundos que el scroll debe permanecer inactivo
 *                                          para considerarse "detenido".
 * @param {number} [options.threshold=5] - La distancia mínima de scroll (en píxeles) para considerar
 *                                        un movimiento significativo y determinar la dirección/distancia.
 * @returns {{ isScrolling: boolean, scrollData: { direction: 'up'|'down'|null, distance: number, startPosition: number, endPosition: number } }}
 *          Un objeto que contiene el estado de si el usuario está scrolleando y los datos del último scroll.
 */
const useAdvancedScrollDetector = ({
  onScrollStart,
  onScrollStop,
  stopDelay = 150,
  threshold = 5,
} = {}) => {
  // Estado para indicar si el usuario está scrolleando activamente
  const [isScrolling, setIsScrolling] = useState(false);

  // Estado para almacenar los datos del scroll cuando se detiene
  const [scrollData, setScrollData] = useState({
    direction: null,
    distance: 0,
    startPosition: 0,
    endPosition: 0,
  });

  // Referencia para el temporizador de detección de "scroll detenido"
  const scrollTimeoutRef = useRef(null);
  // Referencia para almacenar la posición del scroll cuando comienza un nuevo movimiento
  const initialScrollYRef = useRef(window.scrollY);

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;

    // Detectar el inicio del scroll: si no estamos scrolleando y se dispara un evento de scroll,
    // significa que ha comenzado una nueva sesión de scroll.
    if (!isScrolling) {
      setIsScrolling(true);
      initialScrollYRef.current = currentScrollY; // Capturar la posición inicial
      onScrollStart && onScrollStart(currentScrollY);
    }

    // Siempre limpiar el temporizador anterior y establecer uno nuevo.
    // Este temporizador se usará para determinar cuándo el scroll se ha "detenido".
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      // Esta función se ejecuta si no ocurren nuevos eventos de scroll dentro de 'stopDelay' milisegundos.
      // Indica que el scroll se ha detenido.
      setIsScrolling(false);

      const startPos = initialScrollYRef.current;
      const endPos = currentScrollY;
      const distance = Math.abs(endPos - startPos);
      let direction = null;

      // Solo determinar la dirección si el movimiento fue significativo
      if (distance >= threshold) {
        direction = endPos > startPos ? 'down' : 'up';
      }

      const newScrollData = {
        direction,
        distance,
        startPosition: startPos,
        endPosition: endPos,
      };
      setScrollData(newScrollData); // Actualizar el estado con los datos del scroll
      onScrollStop && onScrollStop(newScrollData); // Llamar al callback onScrollStop
    }, stopDelay);

  }, [isScrolling, onScrollStart, onScrollStop, stopDelay, threshold]); // Dependencias del useCallback

  // Efecto para añadir y limpiar el event listener del scroll
  useEffect(() => {
    // Añadir el event listener del scroll de forma pasiva para mejor rendimiento
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Función de limpieza: se ejecuta cuando el componente se desmonta o las dependencias cambian
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current); // Asegurarse de limpiar cualquier temporizador pendiente
      }
    };
  }, [handleScroll]); // Re-ejecutar el efecto si handleScroll cambia (debido a sus dependencias memoizadas)

  return { isScrolling, scrollData };
};

export default useAdvancedScrollDetector;